import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	TextField,
	Typography
} from "@material-ui/core";
import GetChannelImage from "helpers/GetChannelImage";
import { validateEmail } from "helpers/utilities";
import i18n from "i18next";
import React, { useState } from "react";
import { useSelector } from "react-redux";

/**
 * Dialog of the auto send forward of a conversation (forward action with config.autoSend):
 * 	- the agent selects the mail channel to send the forward, displayed as in the action forwardConv (PickerFiles)
 * 	- the "to" of the config is valid: the agent confirms the sending to it
 * 	- otherwise: the agent fills a valid email address
 * @param {object} props the props
 * @param {boolean} props.open the dialog is open
 * @param {string} props.configTo the valid "to" of the action config, undefined if the agent has to fill it
 * @param {Function} props.handleClose called on cancel
 * @param {Function} props.handleValidate called with { channelCode, to } on validation
 * @returns {object} the dialog
 */
const AutoSendConversationDialog = ({ open, configTo, handleClose, handleValidate }) => {
	const { assistantconfig } = useSelector(state => state);

	const [selectedChannel, setSelectedChannel] = useState({});
	const [email, setEmail] = useState("");
	const [showError, setShowError] = useState(false);

	const needEmail = !configTo;
	const trimmedEmail = email.trim();

	// Same order as PickerFiles (favorites first then by name), only the mail channels:
	// the forward is a mail, NYO refuses another channel type
	const channels = (assistantconfig?.channels ?? [])
		.filter(channel => channel.engage === true && channel.type === "ML")
		.sort((a, b) => {
			if (a.favorite === true && b.favorite !== true) return -1;
			if (b.favorite === true && a.favorite !== true) return 1;
			return a.name.localeCompare(b.name);
		});

	/**
	 * Send the selected channel and the recipient, only with a valid email
	 */
	function closeAndValidate() {
		if (!selectedChannel.code) {
			return;
		}
		if (needEmail && !validateEmail(trimmedEmail)) {
			setShowError(true);
			return;
		}
		handleValidate({
			channelCode: selectedChannel.code,
			to: needEmail ? trimmedEmail : undefined
		});
	}

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullWidth
			maxWidth="sm"
			aria-labelledby="auto-send-conversation-title"
		>
			<DialogTitle id="auto-send-conversation-title">{i18n.t("COC.autoForwardTitle")}</DialogTitle>
			<DialogContent>
				<Typography variant="h5" gutterBottom>
					{i18n.t("TabAction.selectChannel")}
				</Typography>
				<Box display="flex" flexWrap="wrap" mb={2}>
					{channels.map(channel => (
						<Box key={channel.code} pr={1} pt={1}>
							<Chip
								label={channel.name}
								style={{ textTransform: "capitalize" }}
								color={channel.code === selectedChannel.code ? "primary" : "default"}
								icon={
									<img
										src={GetChannelImage(
											assistantconfig?.channels,
											channel.code.toUpperCase(),
											channel
										)}
										alt="logo_dropdown"
										width="25"
										height="25"
										className="logo-channel"
									/>
								}
								clickable={true}
								onClick={() => setSelectedChannel(channel)}
							/>
						</Box>
					))}
				</Box>
				{needEmail ? (
					<>
						<DialogContentText>{i18n.t("COC.autoForwardMessage")}</DialogContentText>
						<TextField
							fullWidth
							type="email"
							variant="outlined"
							size="small"
							label={i18n.t("COC.autoForwardEmailLabel")}
							value={email}
							onChange={e => {
								setEmail(e.target.value);
								if (showError) setShowError(false);
							}}
							onKeyDown={e => {
								if (e.key === "Enter") closeAndValidate();
							}}
							error={showError}
							helperText={showError ? i18n.t("COC.autoForwardEmailInvalid") : ""}
						/>
					</>
				) : (
					<DialogContentText>
						{i18n.t("COC.autoForwardConfirmMessage", { email: configTo })}
					</DialogContentText>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} color="primary">
					{i18n.t("COC.cancel")}
				</Button>
				<Button
					onClick={closeAndValidate}
					color="primary"
					disabled={!selectedChannel.code || (needEmail && !trimmedEmail)}
				>
					{i18n.t("COC.autoForwardValidate")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AutoSendConversationDialog;
